import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { isValid, parseISO } from 'date-fns';

import { DropdownOption } from 'components/Dropdown';

import CalculatedStudentAssignmentStatus from 'enums/CalculatedStudentAssignmentStatus';
import TimeframePreset from 'enums/TimeframePreset';

import {
  CourseOptionFragment,
  HomeStudentAssignmentFragment,
  StudentOptionFragment,
} from 'graphql/types/generated';
import { Student_Assignment_Status_Enum } from 'graphql/types/schema';

import { matchesAssignmentStatuses } from 'utils/matchesAssignmentStatuses';

import HomeFiltersState, {
  HomeState,
  RootState,
  TimeframeFilterState,
} from './types';

export const HIDE_COMPLETED_KEY = 'syllabird.home.filters.hideCompleted';
export const TIMEFRAME_FILTER_KEY = 'syllabird.home.filters.timeframe';

export const DEFAULT_TIMEFRAME_FILTER: TimeframeFilterState = {
  preset: TimeframePreset.LastMonth,
  customStart: null,
  customEnd: null,
};

const isValidCustomDate = (value: unknown): value is string | null =>
  value === null || (typeof value === 'string' && isValid(parseISO(value)));

const loadSavedTimeframe = (): TimeframeFilterState => {
  try {
    const savedTimeframe = JSON.parse(
      localStorage?.getItem(TIMEFRAME_FILTER_KEY) ?? 'null'
    );

    if (
      savedTimeframe &&
      Object.values(TimeframePreset).includes(savedTimeframe.preset) &&
      isValidCustomDate(savedTimeframe.customStart) &&
      isValidCustomDate(savedTimeframe.customEnd)
    ) {
      return {
        preset: savedTimeframe.preset,
        customStart: savedTimeframe.customStart,
        customEnd: savedTimeframe.customEnd,
      };
    }
  } catch (error) {
    console.warn('Failed to fetch home filter state from local storage', error);
  }

  return DEFAULT_TIMEFRAME_FILTER;
};

/**
 * Reducers
 */
const loadSavedHideCompleted = (): boolean => {
  try {
    return (
      JSON.parse(localStorage?.getItem(HIDE_COMPLETED_KEY) ?? 'true') ?? true
    );
  } catch (error) {
    console.warn('Failed to fetch home filter state from local storage', error);
  }

  return true;
};

const loadInitialState = (): HomeState => {
  const savedHideCompleted = loadSavedHideCompleted();

  return {
    filters: {
      students: [],
      courses: [],
      subjects: [],
      assignmentTypes: [],
      assignmentStatuses: [],
      hideCompleted: savedHideCompleted,
      timeframe: loadSavedTimeframe(),
    },
    homeStudentAssignments: null,
    selectedHomeStudentAssignmentId: null,
    isDetailsSlideMenuOpen: false,
    isWelcomeToSyllabirdModalOpen: false,
  };
};

export const homeSlice = createSlice({
  name: 'home',
  initialState: loadInitialState(),
  reducers: {
    // Assignments State
    setHomeStudentAssignments: (
      state: HomeState,
      action: PayloadAction<HomeStudentAssignmentFragment[] | null>
    ) => {
      state.homeStudentAssignments = action.payload;
    },
    selectHomeStudentAssignment: (
      state: HomeState,
      action: PayloadAction<string | null>
    ) => {
      state.selectedHomeStudentAssignmentId = action.payload;

      // only open menu if we've actually selected an assignment
      if (action.payload !== null) {
        state.isDetailsSlideMenuOpen = true;
      }
    },

    // Filters State
    setStudentsFilter: (
      state: HomeState,
      action: PayloadAction<DropdownOption<StudentOptionFragment>[]>
    ) => {
      state.filters.students = action.payload;
    },
    setCoursesFilter: (
      state: HomeState,
      action: PayloadAction<DropdownOption<CourseOptionFragment>[]>
    ) => {
      state.filters.courses = action.payload;
    },
    setSubjectsFilter: (
      state: HomeState,
      action: PayloadAction<DropdownOption<string>[]>
    ) => {
      state.filters.subjects = action.payload;
    },
    setAssignmentTypesFilter: (
      state: HomeState,
      action: PayloadAction<DropdownOption<string>[]>
    ) => {
      state.filters.assignmentTypes = action.payload;
    },
    setAssignmentStatusesFilter: (
      state: HomeState,
      action: PayloadAction<
        DropdownOption<
          Student_Assignment_Status_Enum | CalculatedStudentAssignmentStatus
        >[]
      >
    ) => {
      state.filters.assignmentStatuses = action.payload;
    },
    setTimeframeFilter: (
      state: HomeState,
      action: PayloadAction<TimeframeFilterState>
    ) => {
      state.filters.timeframe = action.payload;
    },
    toggleHideCompleted: (state: HomeState) => {
      const hideCompletedNext = !state.filters.hideCompleted;
      state.filters.hideCompleted = hideCompletedNext;

      // Deselect "Complete" if completed, and you opt to "Hide Complete"
      if (hideCompletedNext) {
        state.filters.assignmentStatuses =
          state.filters.assignmentStatuses.filter(
            (status) => status.value !== Student_Assignment_Status_Enum.Complete
          );
      }
    },
    clearFilters: (state: HomeState) => {
      state.filters = {
        ...loadInitialState().filters,
        timeframe: DEFAULT_TIMEFRAME_FILTER,
      };
    },

    // UI State
    setIsDetailsSlideMenuOpen: (
      state: HomeState,
      action: PayloadAction<boolean>
    ) => {
      state.isDetailsSlideMenuOpen = action.payload;
    },
    setIsWelcomeToSyllabirdModalOpen: (
      state: HomeState,
      action: PayloadAction<boolean>
    ) => {
      state.isWelcomeToSyllabirdModalOpen = action.payload;
    },
  },
});

/**
 * Actions
 */
export const {
  setHomeStudentAssignments,
  selectHomeStudentAssignment,
  setStudentsFilter,
  setCoursesFilter,
  setSubjectsFilter,
  setAssignmentTypesFilter,
  setAssignmentStatusesFilter,
  setTimeframeFilter,
  toggleHideCompleted,
  clearFilters,
  setIsDetailsSlideMenuOpen,
  setIsWelcomeToSyllabirdModalOpen,
} = homeSlice.actions;

/**
 * Selectors
 */
const getHome = (state: RootState): HomeState => state.home;

// Filters Selectors
export const getFilters = createSelector(
  getHome,
  (home: HomeState) => home.filters
);

export const getCurrentStudentsFilter = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.students
);

export const getCurrentCoursesFilter = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.courses
);

export const getCurrentSubjectsFilter = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.subjects
);

export const getCurrentAssignmentTypesFilter = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.assignmentTypes
);

export const getCurrentAssignmentStatusesFilter = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.assignmentStatuses
);

export const getHideCompleted = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.hideCompleted
);

export const getTimeframeFilter = createSelector(
  getFilters,
  (filters: HomeFiltersState) => filters.timeframe
);

// Filters that narrow which assignments are listed unlike hideCompleted,
// which only hides finished work, so an empty list under it alone still
// means everything is done rather than filtered out
export const getHasNarrowingFiltersApplied = createSelector(
  getFilters,
  (filters: HomeFiltersState) => {
    const { students, courses, subjects, assignmentTypes, assignmentStatuses } =
      filters;

    return (
      (students?.length ?? 0) +
        (courses?.length ?? 0) +
        (subjects?.length ?? 0) +
        (assignmentTypes?.length ?? 0) +
        (assignmentStatuses?.length ?? 0) >
      0
    );
  }
);

export const getNumFiltersApplied = createSelector(
  getFilters,
  (filters: HomeFiltersState) => {
    const {
      students,
      courses,
      subjects,
      assignmentTypes,
      assignmentStatuses,
      hideCompleted,
      timeframe,
    } = filters;

    return (
      (students?.length ?? 0) +
      (courses?.length ?? 0) +
      (subjects?.length ?? 0) +
      (assignmentTypes?.length ?? 0) +
      (assignmentStatuses?.length ?? 0) +
      (hideCompleted ? 1 : 0) +
      (timeframe.preset !== DEFAULT_TIMEFRAME_FILTER.preset ? 1 : 0)
    );
  }
);

export const getFilteredStudentIds = createSelector(
  getCurrentStudentsFilter,
  (studentsFilter: DropdownOption<StudentOptionFragment>[]) =>
    studentsFilter.map((option) => option.value.id)
);

export const getFilteredCourseIds = createSelector(
  getCurrentCoursesFilter,
  (courses: DropdownOption<CourseOptionFragment>[]) =>
    courses.map((option) => option.value.id)
);

export const getFilteredSubjects = createSelector(
  getCurrentSubjectsFilter,
  (subjects: DropdownOption<string>[]) => subjects.map((option) => option.value)
);

export const getFilteredAssignmentTypeIds = createSelector(
  getCurrentAssignmentTypesFilter,
  (assignmentTypes: DropdownOption<string>[]) =>
    assignmentTypes.map((option) => option.value)
);

export const getFilteredAssignmentStatuses = createSelector(
  getCurrentAssignmentStatusesFilter,
  (
    assignmentStatuses: DropdownOption<
      Student_Assignment_Status_Enum | CalculatedStudentAssignmentStatus
    >[]
  ) => assignmentStatuses.map((option) => option.value)
);

// Assignment List Selectors
export const getHomeStudentAssignments = createSelector(
  getHome,
  (home: HomeState) => home.homeStudentAssignments
);

export const getHomeStudentAssignmentsLength = createSelector(
  getHomeStudentAssignments,
  (studentAssignments: HomeStudentAssignmentFragment[] | null) =>
    studentAssignments?.length ?? null
);

// UI State Selectors
export const getSelectedHomeStudentAssignmentId = createSelector(
  getHome,
  (home: HomeState) => home.selectedHomeStudentAssignmentId
);

export const getIsDetailsSlideMenuOpen = createSelector(
  getHome,
  (home: HomeState) => home.isDetailsSlideMenuOpen
);

export const getIsWelcomeToSyllabirdModalOpen = createSelector(
  getHome,
  (home: HomeState) => home.isWelcomeToSyllabirdModalOpen
);

export const isSelectedHomeStudentAssignment = (studentAssignmentId: string) =>
  createSelector(
    getSelectedHomeStudentAssignmentId,
    (selectedHomeStudentAssignmentId: string | null) =>
      studentAssignmentId === selectedHomeStudentAssignmentId
  );

export const getFilteredHomeStudentAssignments = createSelector(
  getHomeStudentAssignments,
  getFilteredStudentIds,
  getFilteredCourseIds,
  getFilteredSubjects,
  getFilteredAssignmentTypeIds,
  getFilteredAssignmentStatuses,
  getHideCompleted,
  (
    studentAssignments: HomeStudentAssignmentFragment[] | null,
    filteredStudentIds: string[],
    filteredCourseIds: string[],
    filteredSubjects: string[],
    filteredAssignmentTypeIds: string[],
    filteredAssignmentStatuses: (
      | Student_Assignment_Status_Enum
      | CalculatedStudentAssignmentStatus
    )[],
    hideCompleted: boolean
  ) => {
    const matchesStudentsFilter = (
      studentAssignment: HomeStudentAssignmentFragment
    ) =>
      filteredStudentIds.length === 0 ||
      filteredStudentIds.includes(studentAssignment.student.id);

    const matchesCoursesFilter = (
      studentAssignment: HomeStudentAssignmentFragment
    ) =>
      filteredCourseIds.length === 0 ||
      filteredCourseIds.includes(studentAssignment.assignment.course.id);

    const matchesSubjectsFilter = (
      studentAssignment: HomeStudentAssignmentFragment
    ) => {
      const studentAssignmentSubjects = (studentAssignment.assignment.course
        .subjects ?? []) as string[];

      return (
        filteredSubjects.length === 0 ||
        filteredSubjects.some((subject) =>
          studentAssignmentSubjects.includes(subject)
        )
      );
    };

    const matchesAssignmentTypesFilter = (
      studentAssignment: HomeStudentAssignmentFragment
    ) => {
      return (
        filteredAssignmentTypeIds.length === 0 ||
        (studentAssignment.assignment?.customAssignmentType?.id &&
          filteredAssignmentTypeIds.includes(
            studentAssignment.assignment.customAssignmentType.id
          ))
      );
    };

    const filteredHomeStudentAssignments = studentAssignments
      ?.filter(matchesStudentsFilter)
      .filter(matchesCoursesFilter)
      .filter(matchesSubjectsFilter)
      .filter(matchesAssignmentTypesFilter)
      .filter((studentAssignment) =>
        matchesAssignmentStatuses(
          studentAssignment,
          filteredAssignmentStatuses,
          hideCompleted
        )
      );

    return filteredHomeStudentAssignments ?? null;
  }
);

export const getFilteredHomeStudentAssignmentsLength = createSelector(
  getFilteredHomeStudentAssignments,
  (filteredStudentAssignments: HomeStudentAssignmentFragment[] | null) =>
    filteredStudentAssignments?.length ?? null
);
